import { Head, Link } from "@inertiajs/react"

import { dashboardPath, rootPath } from "@/routes"

import { CategorySection } from "./category-section"
import {
  componentRegistry,
  getCategories,
  getComponentsByCategoryAndGroup,
  getGroupsByCategory,
} from "./registry"

export default function Playground() {
  // Organize components by category and group
  const categories = getCategories()
  const componentsByCategory = categories.map((category) => {
    const groups = getGroupsByCategory(category)
    const groupsWithComponents = groups.reduce(
      (acc, group) => {
        acc[group] = getComponentsByCategoryAndGroup(category, group)
        return acc
      },
      {} as Record<string, typeof componentRegistry>,
    )
    return { category, groups: groupsWithComponents }
  })

  // Calculate total stats
  const totalComponents = componentRegistry.length
  const totalGroups = categories.reduce(
    (sum, cat) => sum + getGroupsByCategory(cat).length,
    0,
  )

  return (
    <>
      <Head title="Playground" />

      <div className="bg-background min-h-screen">
        {/* Header */}
        <header className="bg-background/95 supports-[backdrop-filter]:bg-background/60 sticky top-0 z-10 border-b backdrop-blur">
          <div className="container mx-auto flex h-16 items-center justify-between px-4">
            <div className="flex items-center gap-4">
              <Link href={rootPath()} className="text-xl font-bold">
                Playground
              </Link>
              <span className="bg-muted text-muted-foreground rounded-full px-2.5 py-0.5 text-xs font-medium">
                {totalComponents} components
              </span>
            </div>
            <nav className="flex items-center gap-4">
              <Link
                href={rootPath()}
                className="text-muted-foreground hover:text-foreground text-sm transition-colors"
              >
                Home
              </Link>
              <Link
                href={dashboardPath()}
                className="text-muted-foreground hover:text-foreground text-sm transition-colors"
              >
                Dashboard
              </Link>
            </nav>
          </div>
        </header>

        <main className="container mx-auto px-4 py-8">
          {/* Page header */}
          <div className="mb-8">
            <h1 className="mb-2 text-3xl font-bold">
              Untitled UI Pro Components
            </h1>
            <p className="text-muted-foreground">
              Browse and test all installed components. Each component is
              displayed with error boundaries and can be expanded/collapsed for
              easy navigation.
            </p>
            <div className="text-muted-foreground mt-4 flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <svg className="size-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                </svg>
                <span>{categories.length} categories</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="size-4" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>{totalGroups} groups</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="size-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z" />
                </svg>
                <span>{totalComponents} components</span>
              </div>
            </div>
          </div>

          {/* How to add components */}
          <div className="mb-8 rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-900 dark:bg-blue-950/30">
            <div className="flex gap-3">
              <svg
                className="size-5 shrink-0 text-blue-600 dark:text-blue-400"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                  How to add new components
                </h3>
                <div className="mt-2 text-sm text-blue-800 dark:text-blue-200">
                  <p>Adding a new component to the playground is simple:</p>
                  <ol className="mt-2 list-decimal space-y-1 pl-5">
                    <li>
                      Import your component at the top of{" "}
                      <code className="rounded bg-blue-100 px-1.5 py-0.5 text-xs dark:bg-blue-900">
                        app/frontend/pages/playground/registry.ts
                      </code>
                    </li>
                    <li>
                      Add an entry to the componentRegistry array with metadata
                      (category, group, demo props)
                    </li>
                    <li>
                      That&apos;s it! The component will automatically appear in
                      the correct section
                    </li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          {/* Component categories */}
          <div className="space-y-6">
            {componentsByCategory.map(({ category, groups }) => (
              <CategorySection
                key={category}
                category={category}
                groups={groups}
              />
            ))}
          </div>

          {/* Empty state (shouldn't show if registry has components) */}
          {totalComponents === 0 && (
            <div className="rounded-lg border border-dashed p-12 text-center">
              <div className="bg-muted mx-auto mb-4 flex size-12 items-center justify-center rounded-full">
                <svg
                  className="text-muted-foreground size-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                  />
                </svg>
              </div>
              <h3 className="mb-2 text-lg font-semibold">No components yet</h3>
              <p className="text-muted-foreground text-sm">
                Add components to the registry to see them here
              </p>
            </div>
          )}
        </main>
      </div>
    </>
  )
}
